import { useState, useEffect, useRef, useCallback } from 'react';
import { PitchDetector } from 'pitchy';

/**
 * Custom hook for real-time pitch detection using Web Audio API
 * Implements YIN algorithm via pitchy library
 */
export const usePitchDetection = (options = {}) => {
  const {
    smoothingWindow = 200, // ms
    confidenceThreshold = 0.9,
    minFreq = 70,
    maxFreq = 1200,
    onPitchDetected = null
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [currentPitch, setCurrentPitch] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState(null);

  // Refs for audio context and processing
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const detectorRef = useRef(null);
  const animationFrameRef = useRef(null);
  const pitchHistoryRef = useRef([]);

  // Initialize audio context and pitch detector
  const initialize = useCallback(async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices?.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false
        } 
      });
      micStreamRef.current = stream;

      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 44100
      });
      audioContextRef.current = audioContext;

      // Create analyser
      const analyser = audioContext?.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;

      // Connect microphone to analyser
      const source = audioContext?.createMediaStreamSource(stream);
      source?.connect(analyser);

      // Create pitch detector
      const detector = PitchDetector?.forFloat32Array(analyser?.fftSize);
      detectorRef.current = detector;

      setError(null);
      return true;
    } catch (err) {
      console.error('Error initializing pitch detection:', err);
      setError('Failed to access microphone. Please grant permission and try again.');
      return false;
    }
  }, []);

  // Calculate median from array (for vibrato smoothing)
  const calculateMedian = (arr) => {
    if (arr?.length === 0) return 0;
    const sorted = [...arr]?.sort((a, b) => a - b);
    const mid = Math.floor(sorted?.length / 2);
    return sorted?.length % 2 !== 0 ? sorted?.[mid] : (sorted?.[mid - 1] + sorted?.[mid]) / 2;
  };

  // Convert frequency to MIDI note number
  const freqToMidi = (freq) => {
    return 69 + 12 * Math.log2(freq / 440);
  };

  // Convert MIDI to note name
  const midiToNoteName = (midi) => {
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octave = Math.floor(midi / 12) - 1;
    const note = noteNames?.[Math.round(midi) % 12];
    return `${note}${octave}`;
  };

  // Calculate cents difference
  const calculateCents = (freq, targetFreq) => {
    return 1200 * Math.log2(freq / targetFreq);
  };

  // Process audio and detect pitch
  const detectPitch = useCallback(() => {
    if (!analyserRef?.current || !detectorRef?.current) return;

    const analyser = analyserRef?.current;
    const detector = detectorRef?.current;

    // Get audio data
    const buffer = new Float32Array(analyser.fftSize);
    analyser?.getFloatTimeDomainData(buffer);

    // Detect pitch
    const [pitch, clarity] = detector?.findPitch(buffer, audioContextRef?.current?.sampleRate);

    // Validate detection
    const isValid = clarity > confidenceThreshold && 
                   pitch >= minFreq && 
                   pitch <= maxFreq &&
                   !isNaN(pitch);

    if (isValid) {
      // Add to history for smoothing
      const now = Date.now();
      pitchHistoryRef?.current?.push({ pitch, timestamp: now });

      // Keep only recent history (within smoothing window)
      pitchHistoryRef.current = pitchHistoryRef?.current?.filter(
        p => now - p?.timestamp < smoothingWindow
      );

      // Calculate smoothed pitch (median)
      const recentPitches = pitchHistoryRef?.current?.map(p => p?.pitch);
      const smoothedPitch = calculateMedian(recentPitches);

      // Convert to musical units
      const midiNote = freqToMidi(smoothedPitch);
      const noteName = midiToNoteName(midiNote);

      const pitchData = {
        frequency: smoothedPitch,
        midi: midiNote,
        note: noteName,
        confidence: clarity,
        timestamp: now
      };

      setCurrentPitch(pitchData);
      setConfidence(clarity);

      // Call callback if provided
      if (onPitchDetected) {
        onPitchDetected(pitchData);
      }
    } else {
      setCurrentPitch(null);
      setConfidence(clarity || 0);
    }

    // Continue loop
    animationFrameRef.current = requestAnimationFrame(detectPitch);
  }, [confidenceThreshold, minFreq, maxFreq, smoothingWindow, onPitchDetected]);

  // Start listening
  const startListening = useCallback(async () => {
    if (isListening) return;

    const initialized = audioContextRef?.current || (await initialize());
    if (!initialized) return;

    // Resume audio context if suspended
    if (audioContextRef?.current?.state === 'suspended') {
      await audioContextRef?.current?.resume();
    }

    setIsListening(true);
    detectPitch();
  }, [isListening, initialize, detectPitch]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (!isListening) return;

    // Cancel animation frame
    if (animationFrameRef?.current) {
      cancelAnimationFrame(animationFrameRef?.current);
      animationFrameRef.current = null;
    }

    setIsListening(false);
    setCurrentPitch(null);
    setConfidence(0);
    pitchHistoryRef.current = [];
  }, [isListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      
      // Close audio context
      if (audioContextRef?.current) {
        audioContextRef?.current?.close();
        audioContextRef.current = null;
      }

      // Stop microphone stream
      if (micStreamRef?.current) {
        micStreamRef?.current?.getTracks()?.forEach(track => track?.stop());
        micStreamRef.current = null;
      }
    };
  }, [stopListening]);

  return {
    isListening,
    currentPitch,
    confidence,
    error,
    startListening,
    stopListening,
    freqToMidi,
    midiToNoteName,
    calculateCents
  };
};